// src/components/account/SignupClient.tsx
"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import SafeHtml from "@/components/ui/SafeHtml";
import OtpInput from "@/components/ui/OtpInput";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import { useLayoutData } from "../layout/LayoutDataProvider";
import { apiPath } from "@/lib/apiPath";

const OTP_LEN = 6;

type Props = {
  redirectTo?: string;
  lang?: string;
};

export default function SignupClient({ redirectTo, lang }: Props) {
  const router = useRouter();

  const { templateRoutes, global_text } = useLayoutData();
  const routes = templateRoutes ?? {};

  const loginHref = routes["page-login.php"];
  const accountHref = routes["page-account.php"];

  const termsLegal = global_text?.contact_form?.terms_legal?.trim() ?? "";
  const newsletterLegal = global_text?.contact_form?.newsletter_legal?.trim() ?? "";

  const SIGNUP: Record<string, string> =
    (global_text as { auth?: { signup?: Record<string, string> } } | undefined)
      ?.auth?.signup ?? {};
  const t = (k: string, fallback = "") => SIGNUP[k] || fallback;

  const [step, setStep] = React.useState<"start" | "verify">("start");

  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [pw1, setPw1] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [terms, setTerms] = React.useState(false);
  const [newsletter, setNewsletter] = React.useState(false);

  const [signupId, setSignupId] = React.useState("");
  const [otp, setOtp] = React.useState<string[]>(Array(OTP_LEN).fill(""));

  const [loading, setLoading] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);

  const [topError, setTopError] = React.useState<string | null>(null);

  const [firstNameError, setFirstNameError] = React.useState<string | null>(null);
  const [lastNameError, setLastNameError] = React.useState<string | null>(null);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [pw1Error, setPw1Error] = React.useState<string | null>(null);
  const [pw2Error, setPw2Error] = React.useState<string | null>(null);
  const [termsError, setTermsError] = React.useState<string | null>(null);
  const [codeError, setCodeError] = React.useState<string | null>(null);

  const firstNameRef = React.useRef<HTMLInputElement | null>(null);
  const lastNameRef = React.useRef<HTMLInputElement | null>(null);
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const pw1Ref = React.useRef<HTMLInputElement | null>(null);
  const pw2Ref = React.useRef<HTMLInputElement | null>(null);

  const busy = loading || redirecting;

  React.useEffect(() => {
    if (firstNameError) firstNameRef.current?.focus?.();
    else if (lastNameError) lastNameRef.current?.focus?.();
    else if (emailError) emailRef.current?.focus?.();
    else if (pw1Error) pw1Ref.current?.focus?.();
    else if (pw2Error) pw2Ref.current?.focus?.();
  }, [firstNameError, lastNameError, emailError, pw1Error, pw2Error]);

  function resetMessages() {
    setTopError(null);
    setFirstNameError(null);
    setLastNameError(null);
    setEmailError(null);
    setPw1Error(null);
    setPw2Error(null);
    setTermsError(null);
    setCodeError(null);
  }

  function looksLikeEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function clearOtp() {
    setOtp(Array(OTP_LEN).fill(""));
  }

  function validateStartStep() {
    let ok = true;
    const trimmedEmail = email.trim();

    if (!firstName.trim()) {
      setFirstNameError(t("error_first_name_required", "Please enter your first name."));
      ok = false;
    }

    if (!lastName.trim()) {
      setLastNameError(t("error_last_name_required", "Please enter your last name."));
      ok = false;
    }

    if (!trimmedEmail) {
      setEmailError(t("error_email_required", "Please enter your email."));
      ok = false;
    } else if (!looksLikeEmail(trimmedEmail)) {
      setEmailError(t("error_email_invalid", "Please enter a valid email address."));
      ok = false;
    }

    if (!pw1) {
      setPw1Error(t("error_password_required", "Please enter a password."));
      ok = false;
    } else if (pw1.length < 8) {
      setPw1Error(t("error_password_too_short", "Password must be at least 8 characters."));
      ok = false;
    }

    if (!pw2) {
      setPw2Error(t("error_confirm_password_required", "Please confirm your password."));
      ok = false;
    } else if (pw1 && pw1 !== pw2) {
      setPw2Error(t("error_passwords_mismatch", "Passwords do not match."));
      ok = false;
    }

    if (termsLegal && !terms) {
      setTermsError(t("error_terms_required", "You must accept the terms to create an account."));
      ok = false;
    }

    return ok;
  }

  async function start(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    if (!validateStartStep()) return;

    setLoading(true);

    try {
      const res = await fetch(apiPath("/api/auth/register/start"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(),
          password: pw1,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          display_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          terms,
          newsletter,
          lang,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopError(json?.message ?? t("error_register_start_failed", "Could not start registration."));
        setLoading(false);
        return;
      }

      setSignupId(json.signupId);
      clearOtp();
      setStep("verify");
      setLoading(false);
    } catch {
      setTopError(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function verifySubmit(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    if (!signupId) {
      setTopError(t("error_signup_session_missing", "Registration session missing. Please start over."));
      return;
    }

    const code = otp.join("");

    if (code.length !== OTP_LEN || otp.some((d) => d === "")) {
      setCodeError(t("error_code_incomplete", "Enter the 6-digit code."));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(apiPath("/api/auth/register/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({ signupId, code }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopError(json?.message ?? t("error_code_invalid", "That code didn't work. Try again."));
        setLoading(false);
        return;
      }

      // Fire-and-forget E-goi subscription — mirrors the footer contact form pattern.
      // Runs only after successful OTP verification so abandoned signups don't get subscribed.
      // Silent catch so an E-goi outage never blocks the redirect to the account page.
      if (newsletter && email.trim()) {
        void fetch(apiPath("/api/egoi/subscribe"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            lang,
          }),
        }).catch(() => { });
      }

      const dest = redirectTo || accountHref;

      if (dest) {
        setRedirecting(true);
        router.replace(dest);
        router.refresh();
        return;
      }

      setLoading(false);
    } catch {
      setTopError(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  return (
    <Section>
      <Wrapper>
        <div className="mx-auto max-w-lg">
          <div className="flex flex-col gap-6">
            <h1 className="text-title-1 font-semibold">
              {t("heading", "Create account")}
            </h1>

            {step === "start" ? (
              <form onSubmit={start} noValidate className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="w-full min-w-0">
                    <Input
                      id="signup-first-name"
                      ref={firstNameRef}
                      label={<SafeHtml content={t("first_name_label", "First name")} />}
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstNameError(null);
                        setFirstName(e.target.value);
                      }}
                      autoComplete="given-name"
                      aria-invalid={!!firstNameError || undefined}
                      aria-describedby={
                        firstNameError ? "signup-first-name-error" : undefined
                      }
                      invalid={!!firstNameError}
                      required
                    />

                    {firstNameError && (
                      <small
                        id="signup-first-name-error"
                        role="alert"
                        className="mt-1 block text-body-3 text-fuji-red"
                      >
                        {firstNameError}
                      </small>
                    )}
                  </div>

                  <div className="w-full min-w-0">
                    <Input
                      id="signup-last-name"
                      ref={lastNameRef}
                      label={<SafeHtml content={t("last_name_label", "Last name")} />}
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastNameError(null);
                        setLastName(e.target.value);
                      }}
                      autoComplete="family-name"
                      aria-invalid={!!lastNameError || undefined}
                      aria-describedby={
                        lastNameError ? "signup-last-name-error" : undefined
                      }
                      invalid={!!lastNameError}
                      required
                    />

                    {lastNameError && (
                      <small
                        id="signup-last-name-error"
                        role="alert"
                        className="mt-1 block text-body-3 text-fuji-red"
                      >
                        {lastNameError}
                      </small>
                    )}
                  </div>
                </div>

                <div className="w-full min-w-0">
                  <Input
                    id="signup-email"
                    ref={emailRef}
                    label={<SafeHtml content={t("email_label", "Email")} />}
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmailError(null);
                      setEmail(e.target.value);
                    }}
                    autoComplete="email"
                    aria-invalid={!!emailError || undefined}
                    aria-describedby={emailError ? "signup-email-error" : undefined}
                    invalid={!!emailError}
                    required
                  />

                  {emailError && (
                    <small
                      id="signup-email-error"
                      role="alert"
                      className="mt-1 block text-body-3 text-fuji-red"
                    >
                      {emailError}
                    </small>
                  )}
                </div>

                <div className="w-full min-w-0">
                  <Input
                    id="signup-password"
                    ref={pw1Ref}
                    label={<SafeHtml content={t("password_label", "Password")} />}
                    type="password"
                    value={pw1}
                    onChange={(e) => {
                      setPw1Error(null);
                      setPw1(e.target.value);
                    }}
                    autoComplete="new-password"
                    aria-invalid={!!pw1Error || undefined}
                    aria-describedby={pw1Error ? "signup-pw1-error" : undefined}
                    invalid={!!pw1Error}
                    required
                  />

                  {pw1Error && (
                    <small
                      id="signup-pw1-error"
                      role="alert"
                      className="mt-1 block text-body-3 text-fuji-red"
                    >
                      {pw1Error}
                    </small>
                  )}
                </div>

                <div className="w-full min-w-0">
                  <Input
                    id="signup-password-confirm"
                    ref={pw2Ref}
                    label={<SafeHtml content={t("confirm_password_label", "Confirm password")} />}
                    type="password"
                    value={pw2}
                    onChange={(e) => {
                      setPw2Error(null);
                      setPw2(e.target.value);
                    }}
                    autoComplete="new-password"
                    aria-invalid={!!pw2Error || undefined}
                    aria-describedby={pw2Error ? "signup-pw2-error" : undefined}
                    invalid={!!pw2Error}
                    required
                  />

                  {pw2Error && (
                    <small
                      id="signup-pw2-error"
                      role="alert"
                      className="mt-1 block text-body-3 text-fuji-red"
                    >
                      {pw2Error}
                    </small>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {termsLegal && (
                    <div className="w-full min-w-0">
                      <Checkbox
                        id="signup-terms"
                        name="terms"
                        checked={terms}
                        onChange={(e) => {
                          setTermsError(null);
                          setTerms(e.target.checked);
                        }}
                        label={<SafeHtml content={termsLegal} />}
                      />

                      {termsError && (
                        <small
                          id="signup-terms-error"
                          role="alert"
                          className="mt-1 block text-body-3 text-fuji-red"
                        >
                          {termsError}
                        </small>
                      )}
                    </div>
                  )}

                  {newsletterLegal && (
                    <div className="w-full min-w-0">
                      <Checkbox
                        id="signup-newsletter"
                        name="newsletter"
                        checked={newsletter}
                        onChange={(e) => setNewsletter(e.target.checked)}
                        label={<SafeHtml content={newsletterLegal} />}
                      />
                    </div>
                  )}
                </div>

                {topError && (
                  <p className="m-0 text-body-2 text-fuji-red">{topError}</p>
                )}

                <div className="flex w-full flex-col gap-3">
                  <Button
                    type="submit"
                    className="group w-full"
                    disabled={busy}
                    loading={busy}
                    center
                  >
                    <SafeHtml content={t("submit_button", "Create account")} />
                  </Button>

                  {loginHref && (
                    <Link className="underline text-body-2" href={loginHref}>
                      {t("have_account_link", "Already have an account? Sign in")}
                    </Link>
                  )}
                </div>
              </form>
            ) : (
              <form onSubmit={verifySubmit} noValidate className="mt-6 flex flex-col gap-6">
                <p className="text-body-2">
                  <SafeHtml
                    content={t("otp_sent_to_html", "Enter the 6-digit code sent to <strong>{email}</strong>.").replace("{email}", email)}
                  />
                </p>

                <OtpInput
                  idPrefix="signup-otp"
                  label={t("otp_label", "Verification code")}
                  length={OTP_LEN}
                  value={otp}
                  onChange={setOtp}
                  invalid={!!codeError}
                  error={codeError}
                  onClearError={() => setCodeError(null)}
                />

                {topError && (
                  <p className="m-0 text-body-2 text-fuji-red">{topError}</p>
                )}

                <div className="flex w-full flex-col gap-3">
                  <Button
                    type="submit"
                    className="group w-full"
                    disabled={busy}
                    loading={busy}
                    center
                  >
                    <SafeHtml content={t("verify_button", "Verify and finish")} />
                  </Button>

                  <div className="flex items-center justify-between text-body-2">
                    <button
                      type="button"
                      className={`underline ${busy ? "cursor-not-allowed opacity-60" : ""}`}
                      onClick={() => {
                        if (busy) return;
                        resetMessages();
                        clearOtp();
                        setSignupId("");
                        setStep("start");
                      }}
                      disabled={busy}
                    >
                      {t("restart_button", "Restart")}
                    </button>

                    {loginHref && (
                      <Link className="underline" href={loginHref}>
                        {t("back_to_login_link", "Back to sign in")}
                      </Link>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}